import React, { useEffect } from 'react';
import { Flex } from '@cad-arc';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { Panel } from '../panels/panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { UnitHistoryForm } from './types';
import { last36HoursTimeRangeOption, newestFirstSortTypeOption } from './constants';
import { useSetSelectedUnitHistoryUnitOption } from './context/hooks';
import { useUnitHistorySearchMutation } from './hooks/use-unit-history-search-mutation';
import { useApplicationType } from '../common/hooks/use-application-type';
import { timeRangeValueToDateRange } from './utils';
import { useCurrentUser } from '../current-user/current-user';
import { UnitHistoryHeader } from './components/unit-history-header';
import { UnitHistoryBody } from './components/unit-history-body';
import { FrPage } from '../fr-page-layout/fr-page';

export function UnitHistory({ panel }: { panel?: WorkspacePanel }): React.ReactElement {
    const searchMutation = useUnitHistorySearchMutation();
    const setSelectedUnitHistoryUnitOption = useSetSelectedUnitHistoryUnitOption();
    const { isFirstResponder, isCad } = useApplicationType();
    const location = useLocation();

    const {
        userId,
        firstName: userFirstName,
        lastName: userLastName,
        departmentAgencyId: userAgencyId,
    } = useCurrentUser();

    const form = useForm<UnitHistoryForm>({
        defaultValues: {
            unitOption: null,
            userOption: null,
            timeRangeOption: last36HoursTimeRangeOption,
            dateRange: timeRangeValueToDateRange(last36HoursTimeRangeOption.value),
            sortTypeOption: newestFirstSortTypeOption,
        },
        mode: 'onBlur',
    });

    useEffect(() => {
        if (isFirstResponder && userId && userFirstName && userLastName && userAgencyId) {
            form.setValue('userOption', {
                label: `${userFirstName} ${userLastName}`,
                value: userId,
                agencyId: userAgencyId,
            });
        }
    }, [userId, userFirstName, userLastName, userAgencyId, form, isFirstResponder]);

    useEffect(
        function setSavedUnitHistorySearchData() {
            const savedSearchData: UnitHistoryForm | undefined =
                location.state?.unitHistorySearchData;

            if (savedSearchData) {
                form.reset(savedSearchData);
            }
        },
        [form, location.state?.unitHistorySearchData]
    );

    return (
        <IntegratedFormProvider
            mutation={searchMutation}
            form={form}
            keepInputsEnabledOnMutationLoading
        >
            {!!panel && isCad ? (
                <Panel
                    testId="unit-history-panel"
                    panel={panel}
                    header={<UnitHistoryHeader />}
                    body={<UnitHistoryBody />}
                    onClose={() => setSelectedUnitHistoryUnitOption(null)}
                />
            ) : (
                <FrPage>
                    <Flex flexDirection="column" alignItems="flex-start" width="100%" height="100%">
                        <UnitHistoryHeader />
                        <UnitHistoryBody />
                    </Flex>
                </FrPage>
            )}
        </IntegratedFormProvider>
    );
}
